// src/features/hoa-admin/screens/DashboardPage.tsx
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { usePolling, getPollingQueryOpts, useSelectedBuilding } from "../layout/HoaAdminLayout";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { getDashboardStats } from "@/api/reports";
import { listBuildings } from "@/api/buildings";
import { listAnnouncements } from "@/api/announcements";
import { ANNOUNCEMENT_PRIORITY_TONE } from "@/lib/announcementStyles";
import { VOTING_ENABLED } from "@/lib/featureFlags";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Info,
  Building2,
  Users as UsersIconLucide,
  Wrench,
  CreditCard,
  CalendarDays,
  Megaphone,
  BarChart3,
} from "lucide-react";

/* ------------------ recent announcements ------------------ */
// Real data since M2. This section used to render three hardcoded fixtures
// behind a "coming soon" blur, with a comment claiming announcements had no
// backend -- they have had a working one (tenancy, publishing, broadcast)
// since the M2 milestone, so the dashboard was hiding a shipped feature
// behind a placeholder for something that already existed.
//
// The list serializer carries no `content` field (see
// backend/announcements/serializers.py::AnnouncementListSerializer), so the
// card shows what it actually has -- title, priority, when, who -- rather
// than inventing a body.


/* ------------------ design tokens / helpers ------------------ */
const PRIMARY = "#1F3A93";
/** Quiet outline style (consistent with other pages) */
const QUIET =
  "!bg-white !text-gray-900 !border-black/10 border ring-1 ring-black/5 shadow-sm rounded-xl hover:!bg-gray-50 active:!bg-gray-50";

/* ----------------------------- Page ----------------------------- */
export default function DashboardPage() {
  const nav = useNavigate();
  const { enabled, intervalMs } = usePolling();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  // Lifted into the layout so the header can name the building the admin
  // is looking at, and so the choice survives moving between tabs.
  const { buildingId, setBuildingId } = useSelectedBuilding();
  const locale = language === "es" ? "es-ES" : "en-US";
  const usdFormatter = React.useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }),
    [locale]
  );

  const condosQuery = useQuery({
    queryKey: ["hoa-admin", "buildings"],
    queryFn: listBuildings,
    ...getPollingQueryOpts(false, 0),
  });
  const condos = condosQuery.data || [];

  // Live data from backend
  const dashStats = useQuery({
    queryKey: ["hoa-admin", "dashboard", "stats", buildingId],
    queryFn: () => getDashboardStats(buildingId || undefined),
    ...getPollingQueryOpts(enabled, intervalMs),
  });

  // The three most recent announcements this admin can see. Scoped by the
  // backend to buildings they are hoa_admin of; the building selector above
  // narrows the rest of this page but not this list, which deliberately
  // answers "what have I told people lately" across everything they manage.
  const anns = useQuery({
    queryKey: ["hoa-admin", "dashboard", "announcements"],
    queryFn: listAnnouncements,
    // The full history sorts pinned items first. This card promises recent
    // announcements, so an older pinned item must not hide a new publication.
    select: (rows) => [...rows].sort((a, b) =>
      Date.parse(b.published_at || b.created_at) - Date.parse(a.published_at || a.created_at)
    ).slice(0, 3),
    ...getPollingQueryOpts(enabled, intervalMs),
  });

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Greeting */}
      <Card variant="glass" className="rounded-3xl p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 rounded-2xl p-2.5 bg-white ring-1 ring-black/10 shadow-sm">
              <Info className="w-5 h-5 text-gray-700" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-[18px] sm:text-[20px] font-semibold tracking-[-0.01em] text-gray-900">
                {t("hoa.dashboard.greeting", { name: user?.first_name || t("roles.hoaAdmin") })}
              </h2>
              <p className="text-gray-600 mt-1 text-sm">
                {t("hoa.dashboard.subtitle")}
              </p>
            </div>
          </div>
          {/* Building Selector */}
          <div className="flex-shrink-0 w-full sm:w-64">
            <select
              className="w-full text-base font-medium text-gray-800 border-0 bg-white ring-1 shadow-sm ring-black/10 rounded-2xl px-4 py-3 cursor-pointer hover:ring-[#1F3A93]/50 focus:ring-2 focus:ring-[#1F3A93] transition-all outline-none appearance-none"
              style={{
                backgroundImage: "url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')",
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                backgroundSize: '1.25em 1.25em',
                paddingRight: '2.5rem'
              }}
              value={buildingId}
              onChange={(e) => setBuildingId(e.target.value)}
            >
              <option value="">{t("common.allCondos")}</option>
              {condos.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Overview (uses Stat) */}
      <Card
        variant="glass"
        rail
        railColor="border-blue-500"
        railWidth="border-l-4"
        className="rounded-3xl p-5 sm:p-6"
      >
        <h3 className="font-semibold text-lg tracking-tight text-gray-900">
          {t("hoa.dashboard.overview")}
        </h3>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat
            title={t("hoa.dashboard.units")}
            value={dashStats.isLoading ? "…" : dashStats.data?.counts.units ?? "-"}
            icon={<Building2 className="w-4 h-4" />}
            tone="slate"
          />
          <Stat
            title={t("hoa.dashboard.residents")}
            value={dashStats.isLoading ? "…" : dashStats.data?.counts.residents ?? "-"}
            icon={<UsersIconLucide className="w-4 h-4" />}
            tone="slate"
          />
          <Stat
            title={t("hoa.dashboard.openIssues")}
            value={dashStats.isLoading ? "…" : dashStats.data?.counts.open_issues ?? "-"}
            icon={<Wrench className="w-4 h-4" />}
            tone="amber"
          />
          <Stat
            title={t("hoa.dashboard.pendingPayments")}
            value={dashStats.isLoading ? "…" : dashStats.data?.counts.pending_payments ?? "-"}
            icon={<CreditCard className="w-4 h-4" />}
            tone="rose"
          />
        </div>
      </Card>

      {/* Quick actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <ActionTile
          title={t("hoa.dashboard.quick.create")}
          subtitle={t("hoa.dashboard.quick.announcement")}
          icon={<Megaphone className="w-6 h-6" />}
          onClick={() => nav("/hoa-admin/communications?tab=announcements")}
        />
        {VOTING_ENABLED && (
          <ActionTile
            title={t("hoa.dashboard.quick.start")}
            subtitle={t("hoa.dashboard.quick.poll")}
            icon={<BarChart3 className="w-6 h-6" />}
            onClick={() => nav("/hoa-admin/communications?tab=polling")}
          />
        )}
        <ActionTile
          title={t("hoa.dashboard.quick.review")}
          subtitle={t("hoa.dashboard.quick.payments")}
          icon={<CreditCard className="w-6 h-6" />}
          onClick={() => nav("/hoa-admin/payments")}
        />
        <ActionTile
          title={t("hoa.dashboard.quick.log")}
          subtitle={t("hoa.dashboard.quick.issue")}
          icon={<Wrench className="w-6 h-6" />}
          onClick={() => nav("/hoa-admin/issues")}
        />
      </div>

      {/* Recent Announcements — real data, no longer behind a placeholder */}
      <div className="relative">
      <Card
        variant="glass"
        rail
        railColor="border-amber-500"
        railWidth="border-l-4"
        className="rounded-3xl p-5 sm:p-6"
      >
        <div className="flex items-start gap-4">
          <div className="shrink-0 rounded-2xl p-2.5 bg-white ring-1 ring-black/10 shadow-sm">
            <Megaphone className="w-5 h-5 text-gray-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-lg tracking-tight text-gray-900">
                  {t("hoa.dashboard.recentAnnouncements")}
                </h3>
                <Badge className="bg-white text-black ring-1 ring-black/10">
                  {t("hoa.dashboard.shown", { count: (anns.data ?? []).length })}
                </Badge>
              </div>
              <div className="sm:ml-auto inline-flex items-center gap-1 text-sm text-gray-600">
                <CalendarDays className="w-4 h-4" /> {t("hoa.dashboard.updatedJustNow")}
              </div>
            </div>

            <div className="mt-3">
              {anns.isLoading ? (
                <ListSkeleton rows={3} />
              ) : anns.isError ? (
                <p role="alert" className="text-sm text-red-700">{t("common.error")}</p>
              ) : (anns.data ?? []).length === 0 ? (
                <EmptyList text={t("hoa.dashboard.noAnnouncements")} />
              ) : (
                <ul className="space-y-2">
                  {anns.data!.map((a) => (
                    <li
                      key={a.id}
                      className="rounded-xl bg-white/80 backdrop-blur ring-1 ring-black/5 px-4 py-3"
                    >
                      <div className="flex items-start gap-3">
                        {/* Priority drives the colour, on the same scale the
                            announcements page and the emails use. */}
                        <div
                          className="rounded-2xl p-2"
                          style={{
                            background: ANNOUNCEMENT_PRIORITY_TONE[a.priority].bg,
                            color: ANNOUNCEMENT_PRIORITY_TONE[a.priority].fg,
                          }}
                        >
                          <Megaphone className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            {/* A real title, not a translation key: these are
                                user-written strings now, not fixtures. */}
                            <h4 className="truncate text-[15px] font-semibold text-gray-900">
                              {a.title}
                            </h4>
                            <span className="shrink-0 text-xs text-gray-600">
                              {new Date(a.published_at).toLocaleDateString(locale)}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-700/90">
                            {a.priority_display}
                            {a.is_pinned ? ` · ${t("hoa.announcements.pinned")}` : ""}
                            {a.author_name ? ` · ${a.author_name}` : ""}
                          </p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-4 flex justify-end">
                <Button
                  variant="outline"
                  className={`rounded-2xl px-4 py-2.5 ${QUIET}`}
                  onClick={() =>
                    nav("/hoa-admin/communications?tab=announcements")
                  }
                >
                  {t("hoa.dashboard.viewAll")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Card>
      </div>

      {/* Polls/voting is hidden until the next milestone
          (lib/featureFlags.ts). The section is not rendered at all rather
          than shown behind a "coming soon" blur: a blurred placeholder
          still advertises a feature, and this one also sat on top of
          hardcoded fixtures. */}
      {/* Issues snapshot */}
      <Card
        variant="glass"
        rail
        railColor="border-amber-500"
        railWidth="border-l-4"
        className="rounded-3xl p-5 sm:p-6"
      >
        <div className="flex items-start gap-4">
          <div className="shrink-0 rounded-2xl p-2.5 bg-white ring-1 ring-black/10 shadow-sm">
            <Wrench className="w-5 h-5 text-gray-700" />
          </div>
          {/* ///////////////// */}

          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-lg tracking-tight text-gray-900">
                  {t("hoa.dashboard.issues")}
                </h3>

                {!dashStats.isLoading && dashStats.data && (
                  <Badge className="bg-white text-black ring-1 ring-black/10">
                    {t("hoa.dashboard.openCount", {
                      count: dashStats.data.counts.open_issues,
                    })}
                  </Badge>
                )}
              </div>

              <div className="sm:ml-auto inline-flex items-center gap-1 text-sm text-gray-600">
                <CalendarDays className="w-4 h-4" /> {t("hoa.dashboard.updatedJustNow")}
              </div>
            </div>
          </div>
        </div>
        <div className="mt-2">
          {dashStats.isLoading ? (
            <div className="mt-3">
              <ListSkeleton rows={2} />
            </div>
          ) : (
            <div className="mt-3 space-y-3">
              <div className="rounded-xl bg-gray-50 ring-1 ring-black/5 px-4 py-3 text-sm text-gray-600">
                {t("hoa.dashboard.openCount", { count: dashStats.data?.counts.open_issues ?? 0 })}
              </div>
            </div>
          )}

          <div className="mt-2 flex justify-end">
            <Button
              variant="outline"
              className={`rounded-2xl px-4 py-2.5 ${QUIET}`}
              onClick={() => nav("/hoa-admin/issues")}
            >
              {t("hoa.dashboard.openBoard")}
            </Button>
          </div>
        </div>
      </Card>

      {/* Payments snapshot */}
      <Card
        variant="glass"
        rail
        railColor={
          (dashStats.data?.payments_snapshot.pending_proofs ?? 0) > 0
            ? "border-amber-500"
            : "border-emerald-500"
        }
        railWidth="border-l-4"
        className="rounded-3xl p-5 sm:p-6"
      >
        <div className="flex items-start gap-4">
          <div className="shrink-0 rounded-2xl p-2.5 bg-white ring-1 ring-black/10 shadow-sm">
            <CreditCard className="w-5 h-5 text-gray-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-lg tracking-tight text-gray-900">
                  {t("hoa.dashboard.payments")}
                </h3>
                {!dashStats.isLoading && dashStats.data && (
                  <Badge className="bg-white text-black ring-1 ring-black/10">
                    {t("hoa.dashboard.pendingProofs", {
                      count: dashStats.data.payments_snapshot.pending_proofs,
                    })}
                  </Badge>
                )}
              </div>
              <div className="sm:ml-auto inline-flex items-center gap-1 text-sm text-gray-600">
                <CalendarDays className="w-4 h-4" /> {t("hoa.dashboard.thisMonth")}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-2">
          {dashStats.isLoading ? (
            <div className="mt-3">
              <ListSkeleton rows={2} />
            </div>
          ) : dashStats.data ? (
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              <div className="rounded-2xl px-4 py-3 text-sm bg-gray-50 ring-1 ring-black/5">
                <div className="font-medium text-gray-900">
                  {t("hoa.dashboard.approvedThisWeek")}
                </div>
                <div className="text-gray-700/80">
                  {dashStats.data.payments_snapshot.approved_this_month}
                </div>
              </div>
              <div className="rounded-2xl px-4 py-3 text-sm bg-gray-50 ring-1 ring-black/5">
                <div className="font-medium text-gray-900">
                  {t("hoa.dashboard.rejectedThisWeek")}
                </div>
                <div className="text-gray-700/80">
                  {dashStats.data.payments_snapshot.rejected_this_month}
                </div>
              </div>
              <div className="sm:col-span-2 rounded-2xl px-4 py-3 text-sm bg-gray-50 ring-1 ring-black/5">
                <div className="font-medium text-gray-900">
                  {t("hoa.dashboard.collectedThisMonth")}
                </div>
                <div className="text-gray-900 text-[18px] font-semibold">
                  {usdFormatter.format(
                    dashStats.data.payments_snapshot.total_collected_this_month
                  )}
                </div>
              </div>
              <div className="sm:col-span-2 flex justify-end">
                <Button
                  className="rounded-2xl px-3 py-2 h-auto text-white"
                  style={{ backgroundColor: PRIMARY }}
                  onClick={() => nav("/hoa-admin/payments")}
                >
                  {t("hoa.dashboard.reviewPayments")}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </Card>
    </div>
  );
}

/* --------------------- Reusable pieces --------------------- */
function ListSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="animate-pulse rounded-xl bg-white/70 backdrop-blur ring-1 ring-black/5 px-4 py-3"
        >
          <div className="h-3.5 w-1/2 rounded bg-gray-200" />
          <div className="mt-2 h-3 w-2/3 rounded bg-gray-200" />
        </div>
      ))}
    </div>
  );
}
function EmptyList({ text }: { text: string }) {
  return (
    <div className="rounded-xl border border-dashed border-black/10 bg-white/60 backdrop-blur p-6 text-center text-sm text-gray-600">
      {text}
    </div>
  );
}
/* ---- ActionTile (Apple-ish quick action) ---- */
function ActionTile({
  title,
  subtitle,
  icon,
  onClick,
}: {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      title={`${title} ${subtitle}`}
      className={[
        "rounded-2xl text-left transition will-change-transform",
        "bg-white/80 backdrop-blur-xl ring-1 ring-black/5 shadow-lg shadow-black/10 hover:shadow-xl",
        "active:scale-[0.99] px-4 py-4 flex items-center gap-3",
      ].join(" ")}
    >
      <div className="shrink-0 rounded-2xl p-2 bg-white ring-1 ring-black/10 shadow-sm">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="font-semibold text-gray-900">{title}</div>
        <div className="text-sm text-gray-600">{subtitle}</div>
      </div>
    </button>
  );
}

/* ---- NEW: Stat (for Overview KPIs) ---- */
function Stat({
  title,
  value,
  icon,
  tone = "slate",
}: {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  tone?: "slate" | "emerald" | "amber" | "rose";
}) {
  const tones: Record<string, string> = {
    slate: "bg-gray-50 ring-gray-200 text-gray-900",
    emerald: "bg-emerald-50 ring-emerald-200 text-emerald-900",
    amber: "bg-amber-50 ring-amber-200 text-amber-900",
    rose: "bg-rose-50 ring-rose-200 text-rose-900",
  };
  return (
    <div className={`rounded-2xl px-4 py-3 ring-1 ${tones[tone]}`}>
      <div className="flex items-center gap-2 text-sm">
        <span className="grid place-items-center rounded-lg bg-white/80 ring-1 ring-black/5 p-1.5">
          {icon}
        </span>
        <span className="font-medium">{title}</span>
      </div>
      <div className="mt-1 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

/* The old getTypeBg/getTypeFg pair keyed off a mock-only `type` field
   ("maintenance" | "meeting" | "policy" | ...) that the real Announcement
   model has never had. Colour now comes from `priority`, via the shared
   ANNOUNCEMENT_PRIORITY_TONE scale the announcements page and the emails
   already use. */
